import { useState, useEffect } from 'react';
import { isNative, isIOS, isAndroid } from '@/lib/platform';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';
type Browser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung' | 'other';

export function usePWA() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState<Platform>('unknown');
  const [browser, setBrowser] = useState<Browser>('other');

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detect platform - prioritize Capacitor native detection
    if (isNative()) {
      // Running in native Capacitor app
      setPlatform(isIOS() ? 'ios' : isAndroid() ? 'android' : 'desktop');
    } else if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Detect browser
    // Note: On iOS, all browsers use WebKit, but we can detect which browser shell
    if (/crios/.test(userAgent)) {
      // Chrome on iOS
      setBrowser('chrome');
    } else if (/fxios/.test(userAgent)) {
      // Firefox on iOS
      setBrowser('firefox');
    } else if (/edgios/.test(userAgent)) {
      // Edge on iOS
      setBrowser('edge');
    } else if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
      // Safari (not Chrome)
      setBrowser('safari');
    } else if (/samsungbrowser/.test(userAgent)) {
      // Samsung Internet
      setBrowser('samsung');
    } else if (/chrome/.test(userAgent)) {
      // Chrome on other platforms
      setBrowser('chrome');
    } else if (/firefox/.test(userAgent)) {
      // Firefox
      setBrowser('firefox');
    } else if (/edg/.test(userAgent)) {
      // Edge
      setBrowser('edge');
    } else {
      setBrowser('other');
    }

    // Check if already installed - includes native Capacitor apps
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || (navigator as any).standalone 
      || document.referrer.includes('android-app://')
      || isNative(); // Native Capacitor apps are always "installed"
    setIsInstalled(isStandalone);

    // Listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) return false;
    
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstallPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Install error:', error);
      return false;
    }
  };

  return {
    installPrompt,
    isInstalled,
    platform,
    browser,
    canInstall: !!installPrompt,
    triggerInstall,
  };
}
